
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { CalendarGuard } from '../types';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

export const useGuards = (teamId: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const [availableGuards, setAvailableGuards] = useState<CalendarGuard[]>([]);
  const [allGuards, setAllGuards] = useState<CalendarGuard[]>([]);
  const { t } = useLanguage();
  
  useEffect(() => {
    const fetchGuards = async () => {
      if (!teamId) {
        return;
      }
      
      setIsLoading(true);
      
      try {
        // Use a different query approach to avoid the relation error
        const { data: teamMembers, error: teamMemberError } = await supabase
          .from('team_members')
          .select('profile_id')
          .eq('team_id', teamId);
          
        if (teamMemberError) {
          throw teamMemberError;
        }
        
        if (!teamMembers || teamMembers.length === 0) {
          setAvailableGuards([]);
          return;
        }
        
        // Get profile IDs from team members
        const profileIds = teamMembers.map(member => member.profile_id);
        
        // Query ALL profiles for display purposes (including archived)
        const { data: allProfiles, error: allProfilesError } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, full_name, status')
          .in('id', profileIds);
          
        if (allProfilesError) {
          throw allProfilesError;
        }
        
        // Query only active profiles for dropdowns
        const { data: activeProfiles, error: activeProfilesError } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, full_name, status')
          .in('id', profileIds)
          .eq('status', 'active');
          
        if (activeProfilesError) {
          throw activeProfilesError;
        }
        
        // Process ALL guards (including archived) for display
        const processGuards = (profiles: any[]) => {
          return profiles.map(profile => {
            // Always construct name with last name first format, regardless of full_name field
            const lastNameFirst = `${profile.last_name || ''} ${profile.first_name || ''}`.trim();
            return {
              id: profile.id,
              name: lastNameFirst,
              fullName: lastNameFirst,
              first_name: profile.first_name || '',
              last_name: profile.last_name || '',
              full_name: lastNameFirst,
              status: profile.status
            };
          }).sort((a, b) => {
            const lastNameA = (a.last_name || '').toLowerCase();
            const lastNameB = (b.last_name || '').toLowerCase();
            return lastNameA.localeCompare(lastNameB);
          });
        };
        
        const allGuardsList = allProfiles ? processGuards(allProfiles) : [];
        const activeGuardsList = activeProfiles ? processGuards(activeProfiles) : [];
        
        console.log('=== ALL GUARDS (including archived) ===');
        console.log('Total guards:', allGuardsList.length);
        console.log('Active guards for dropdown:', activeGuardsList.length);
        
        setAllGuards(allGuardsList);
        setAvailableGuards(activeGuardsList);
      } catch (error: any) {
        console.error('Error fetching guards:', error);
        toast.error(t('errorFetchingGuards') || 'Error fetching guards');
        setAvailableGuards([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchGuards();
  }, [teamId, t]);

  // Listen for guard updates to refresh the guards list
  useEffect(() => {
    const handleRefetch = () => {
      console.log('Refetching guards due to guard update event');
      // Re-trigger the fetchGuards effect by updating the dependencies
      if (teamId) {
        const fetchGuards = async () => {
          setIsLoading(true);
          
          try {
            const { data: teamMembers, error: teamMemberError } = await supabase
              .from('team_members')
              .select('profile_id')
              .eq('team_id', teamId);
              
            if (teamMemberError) {
              throw teamMemberError;
            }
            
            if (!teamMembers || teamMembers.length === 0) {
              setAvailableGuards([]);
              return;
            }
            
            const profileIds = teamMembers.map(member => member.profile_id);
            
            // Query ALL profiles for display purposes (including archived)
            const { data: allProfiles, error: allProfilesError } = await supabase
              .from('profiles')
              .select('id, first_name, last_name, full_name, status')
              .in('id', profileIds);
              
            if (allProfilesError) {
              throw allProfilesError;
            }
            
            // Query only active profiles for dropdowns
            const { data: activeProfiles, error: activeProfilesError } = await supabase
              .from('profiles')
              .select('id, first_name, last_name, full_name, status')
              .in('id', profileIds)
              .eq('status', 'active');
              
            if (activeProfilesError) {
              throw activeProfilesError;
            }
            
            // Process guards function
            const processGuards = (profiles: any[]) => {
              return profiles.map(profile => {
                // Always construct name with last name first format, regardless of full_name field
                const lastNameFirst = `${profile.last_name || ''} ${profile.first_name || ''}`.trim();
                return {
                  id: profile.id,
                  name: lastNameFirst,
                  fullName: lastNameFirst,
                  first_name: profile.first_name || '',
                  last_name: profile.last_name || '',
                  full_name: lastNameFirst,
                  status: profile.status
                };
              }).sort((a, b) => {
                const lastNameA = (a.last_name || '').toLowerCase();
                const lastNameB = (b.last_name || '').toLowerCase();
                return lastNameA.localeCompare(lastNameB);
              });
            };
            
            const allGuardsList = allProfiles ? processGuards(allProfiles) : [];
            const activeGuardsList = activeProfiles ? processGuards(activeProfiles) : [];
            
            setAllGuards(allGuardsList);
            setAvailableGuards(activeGuardsList);
          } catch (error: any) {
            console.error('Error refetching guards:', error);
          } finally {
            setIsLoading(false);
          }
        };
        
        fetchGuards();
      }
    };

    document.addEventListener('refetch-schedules', handleRefetch);

    return () => {
      document.removeEventListener('refetch-schedules', handleRefetch);
    };
  }, [teamId, t]);
  
  return {
    availableGuards,
    allGuards,
    isLoading
  };
};
